import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  GitMerge,
  Info,
} from "lucide-react";

const MergeIntervalsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Basic overlap",
      intervals: [
        [1, 3],
        [2, 6],
        [8, 10],
        [15, 18],
      ],
      expected: [
        [1, 6],
        [8, 10],
        [15, 18],
      ],
    },
    {
      id: 2,
      name: "All overlap",
      intervals: [
        [1, 4],
        [4, 5],
      ],
      expected: [[1, 5]],
    },
    {
      id: 3,
      name: "No overlap",
      intervals: [
        [1, 2],
        [3, 4],
        [5, 6],
      ],
      expected: [
        [1, 2],
        [3, 4],
        [5, 6],
      ],
    },
    {
      id: 4,
      name: "Contained intervals",
      intervals: [
        [1, 4],
        [2, 3],
      ],
      expected: [[1, 4]],
    },
    {
      id: 5,
      name: "Multiple overlaps",
      intervals: [
        [1, 4],
        [0, 2],
        [3, 5],
      ],
      expected: [[0, 5]],
    },
    {
      id: 6,
      name: "Single interval",
      intervals: [[1, 5]],
      expected: [[1, 5]],
    },
    {
      id: 7,
      name: "Unsorted intervals",
      intervals: [
        [1, 3],
        [8, 10],
        [2, 6],
        [15, 18],
      ],
      expected: [
        [1, 6],
        [8, 10],
        [15, 18],
      ],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customIntervals, setCustomIntervals] = useState("");
  const [intervals, setIntervals] = useState(testCases[0].intervals);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function merge(intervals):", indent: 0 },
    { line: "// Sort intervals by start time", indent: 1 },
    { line: "intervals.sort((a, b) => a[0] - b[0])", indent: 1 },
    { line: "", indent: 0 },
    { line: "result = []", indent: 1 },
    { line: "currentInterval = intervals[0]", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 1 to intervals.length:", indent: 1 },
    { line: "nextInterval = intervals[i]", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Check if intervals overlap", indent: 2 },
    { line: "if currentInterval[1] >= nextInterval[0]:", indent: 2 },
    { line: "// Merge by extending the end", indent: 3 },
    {
      line: "currentInterval[1] = max(currentInterval[1], nextInterval[1])",
      indent: 3,
    },
    { line: "else:", indent: 2 },
    { line: "// No overlap, save current and move to next", indent: 3 },
    { line: "result.push(currentInterval)", indent: 3 },
    { line: "currentInterval = nextInterval", indent: 3 },
    { line: "", indent: 0 },
    { line: "// Add the last interval", indent: 1 },
    { line: "result.push(currentInterval)", indent: 1 },
    { line: "return result", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (inputIntervals) => {
    if (inputIntervals.length === 0) {
      return [
        {
          step: 0,
          intervals: [],
          result: [],
          phase: "complete",
          description: "Empty intervals array",
          highlightLine: 0,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    let stepCount = 0;

    // Step 1: Show original intervals
    allSteps.push({
      step: stepCount++,
      intervals: [...inputIntervals],
      result: [],
      phase: "init",
      description: "Original intervals",
      highlightLine: 0,
    });

    // Step 2: Sort intervals
    const sorted = [...inputIntervals].sort((a, b) => a[0] - b[0]);
    const needsSorting =
      JSON.stringify(sorted) !== JSON.stringify(inputIntervals);

    if (needsSorting) {
      allSteps.push({
        step: stepCount++,
        intervals: sorted,
        result: [],
        phase: "sort",
        description: `Sort intervals by start time`,
        highlightLine: 2,
        sorted: true,
      });
    }

    // Step 3: Initialize
    const result = [];
    let currentInterval = [...sorted[0]];

    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      result: [],
      currentInterval: [...currentInterval],
      currentIndex: 0,
      phase: "init-merge",
      description: `Initialize with first interval [${currentInterval[0]},${currentInterval[1]}]`,
      highlightLine: 5,
    });

    // Step 4: Process each interval
    for (let i = 1; i < sorted.length; i++) {
      const nextInterval = sorted[i];

      // Show comparison
      allSteps.push({
        step: stepCount++,
        intervals: sorted,
        result: [...result],
        currentInterval: [...currentInterval],
        nextInterval: [...nextInterval],
        currentIndex: i,
        phase: "compare",
        description: `Compare [${currentInterval[0]},${currentInterval[1]}] with [${nextInterval[0]},${nextInterval[1]}]`,
        highlightLine: 11,
        comparison: {
          overlap: currentInterval[1] >= nextInterval[0],
          condition: `${currentInterval[1]} >= ${nextInterval[0]}`,
        },
      });

      if (currentInterval[1] >= nextInterval[0]) {
        // Merge intervals
        const oldEnd = currentInterval[1];
        currentInterval[1] = Math.max(currentInterval[1], nextInterval[1]);

        allSteps.push({
          step: stepCount++,
          intervals: sorted,
          result: [...result],
          currentInterval: [...currentInterval],
          currentIndex: i,
          phase: "merge",
          description: `Merge: extend end from ${oldEnd} to ${currentInterval[1]}`,
          highlightLine: 13,
          mergeAnimation: {
            from: [currentInterval[0], oldEnd],
            to: [...currentInterval],
            merged: nextInterval,
          },
        });
      } else {
        // No overlap, save current and move to next
        result.push([...currentInterval]);

        allSteps.push({
          step: stepCount++,
          intervals: sorted,
          result: [...result],
          currentInterval: [...currentInterval],
          currentIndex: i,
          phase: "save",
          description: `No overlap: save [${currentInterval[0]},${currentInterval[1]}] to result`,
          highlightLine: 16,
        });

        currentInterval = [...nextInterval];

        allSteps.push({
          step: stepCount++,
          intervals: sorted,
          result: [...result],
          currentInterval: [...currentInterval],
          currentIndex: i,
          phase: "move",
          description: `Move to next interval [${currentInterval[0]},${currentInterval[1]}]`,
          highlightLine: 17,
        });
      }
    }

    // Add the last interval
    result.push(currentInterval);
    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      result: [...result],
      phase: "final",
      description: `Add last interval [${currentInterval[0]},${currentInterval[1]}] to result`,
      highlightLine: 20,
    });

    // Final state
    allSteps.push({
      step: stepCount++,
      intervals: sorted,
      result: [...result],
      phase: "complete",
      description: `Complete! Merged ${sorted.length} intervals into ${result.length}`,
      highlightLine: 21,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(intervals);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [intervals]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setIntervals(testCases[index].intervals);
  };

  const parseIntervals = (str) => {
    if (!str.trim()) return [];

    const intervalMatches = str.match(/\[?\s*\d+\s*,\s*\d+\s*\]?/g);
    if (!intervalMatches) return null;

    const parsed = [];
    for (const match of intervalMatches) {
      const nums = match.match(/\d+/g);
      if (nums && nums.length === 2) {
        const start = parseInt(nums[0]);
        const end = parseInt(nums[1]);
        if (start > end) return null;
        parsed.push([start, end]);
      }
    }

    return parsed.length > 0 ? parsed : null;
  };

  const addCustomTestCase = () => {
    try {
      const parsedIntervals = parseIntervals(customIntervals);

      if (!parsedIntervals) {
        alert("Invalid format. Use: [[1,3],[2,6],[8,10]]");
        return;
      }

      // Calculate expected result
      const sorted = [...parsedIntervals].sort((a, b) => a[0] - b[0]);
      const expected = [];
      let current = [...sorted[0]];

      for (let i = 1; i < sorted.length; i++) {
        if (current[1] >= sorted[i][0]) {
          current[1] = Math.max(current[1], sorted[i][1]);
        } else {
          expected.push(current);
          current = [...sorted[i]];
        }
      }
      expected.push(current);

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        intervals: parsedIntervals,
        expected: expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomIntervals("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  // Generate timeline visualization
  const generateTimeline = () => {
    if (!currentStepData?.intervals) return null;

    const allIntervals = [...currentStepData.intervals];
    if (currentStepData.currentInterval) {
      allIntervals.push(currentStepData.currentInterval);
    }

    const maxEnd = Math.max(...allIntervals.flat());
    const scale = 100 / (maxEnd + 2);

    return { maxEnd, scale };
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <GitMerge className="text-blue-500" size={24} />
        Merge Intervals
      </h2>

      {/* Problem Statement */}
      <div
        className={`mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200 ${
          showProblem ? "" : "hidden"
        }`}
      >
        <div className="flex items-start gap-2">
          <Info className="text-blue-600 mt-1" size={16} />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">
              Problem Statement
            </h3>
            <p className="text-sm text-blue-800 mb-2">
              Given an array of intervals where intervals[i] = [start_i, end_i],
              merge all overlapping intervals, and return an array of the
              non-overlapping intervals that cover all the intervals in the
              input.
            </p>
            <div className="mt-2 p-2 bg-white rounded">
              <p className="text-xs font-medium text-gray-700 mb-1">Example:</p>
              <p className="text-xs font-mono text-gray-600">
                Input: intervals = [[1,3],[2,6],[8,10],[15,18]]
                <br />
                Output: [[1,6],[8,10],[15,18]]
                <br />
                Explanation: Since intervals [1,3] and [2,6] overlap, merge them
                into [1,6].
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowProblem(false)}
            className="text-blue-600 hover:text-blue-800"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {!showProblem && (
        <button
          onClick={() => setShowProblem(true)}
          className="mb-4 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <Info size={14} />
          Show problem statement
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      Input:{" "}
                      {tc.intervals
                        .map((int) => `[${int[0]},${int[1]}]`)
                        .join(", ")}
                    </div>
                    <div className="text-xs opacity-75">
                      Output:{" "}
                      {tc.expected
                        .map((int) => `[${int[0]},${int[1]}]`)
                        .join(", ")}
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customIntervals}
                onChange={(e) => setCustomIntervals(e.target.value)}
                placeholder="Enter intervals: [[1,3],[2,6],[8,10]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Interval Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Comparison Display */}
            {currentStepData?.comparison && (
              <div className="mb-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  Overlap check: {currentStepData.comparison.condition} ={" "}
                  {currentStepData.comparison.overlap
                    ? "true (merge)"
                    : "false (no merge)"}
                </p>
              </div>
            )}

            {/* Timeline Visualization */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Timeline View:
              </p>
              <div className="relative h-48 bg-gray-50 rounded p-2">
                {generateTimeline() && (
                  <>
                    {/* Grid lines */}
                    {[...Array(Math.floor(generateTimeline().maxEnd + 2))].map(
                      (_, i) => (
                        <div
                          key={i}
                          className="absolute top-0 bottom-0 border-l border-gray-300"
                          style={{ left: `${i * generateTimeline().scale}%` }}
                        >
                          <span className="absolute -bottom-5 -left-2 text-xs text-gray-500">
                            {i}
                          </span>
                        </div>
                      )
                    )}

                    {/* Original intervals */}
                    {currentStepData.intervals.map((interval, idx) => (
                      <div
                        key={`interval-${idx}`}
                        className={`absolute h-8 rounded transition-all duration-300 ${
                          idx === currentStepData.currentIndex
                            ? "bg-yellow-400 border-2 border-yellow-600"
                            : currentStepData.sorted &&
                              idx < (currentStepData.currentIndex || 0)
                            ? "bg-gray-300 border border-gray-400 opacity-50"
                            : "bg-blue-300 border border-blue-400"
                        }`}
                        style={{
                          left: `${interval[0] * generateTimeline().scale}%`,
                          width: `${
                            (interval[1] - interval[0]) *
                            generateTimeline().scale
                          }%`,
                          top: `${10 + idx * 12}px`,
                        }}
                      >
                        <span className="text-xs text-white px-1">
                          [{interval[0]},{interval[1]}]
                        </span>
                      </div>
                    ))}

                    {/* Current interval being processed */}
                    {currentStepData.currentInterval && (
                      <div
                        className="absolute h-10 bg-green-400 border-2 border-green-600 rounded"
                        style={{
                          left: `${
                            currentStepData.currentInterval[0] *
                            generateTimeline().scale
                          }%`,
                          width: `${
                            (currentStepData.currentInterval[1] -
                              currentStepData.currentInterval[0]) *
                            generateTimeline().scale
                          }%`,
                          bottom: "40px",
                        }}
                      >
                        <span className="text-xs text-white px-1 font-medium">
                          Current: [{currentStepData.currentInterval[0]},
                          {currentStepData.currentInterval[1]}]
                        </span>
                      </div>
                    )}

                    {/* Merge animation */}
                    {currentStepData.mergeAnimation && (
                      <div
                        className="absolute h-2 bg-red-500 rounded animate-pulse"
                        style={{
                          left: `${
                            currentStepData.mergeAnimation.from[0] *
                            generateTimeline().scale
                          }%`,
                          width: `${
                            (currentStepData.mergeAnimation.to[1] -
                              currentStepData.mergeAnimation.from[0]) *
                            generateTimeline().scale
                          }%`,
                          bottom: "35px",
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Result Array */}
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-700 mb-2">Result:</p>
              <div className="flex flex-wrap gap-1">
                {currentStepData?.result?.length > 0 ? (
                  currentStepData.result.map((interval, idx) => (
                    <div
                      key={idx}
                      className={`px-2 py-1 rounded text-xs font-mono transition-all ${
                        currentStepData.phase === "save" &&
                        idx === currentStepData.result.length - 1
                          ? "bg-green-200 border-2 border-green-500 animate-pulse"
                          : "bg-green-100 border border-green-300"
                      }`}
                    >
                      [{interval[0]},{interval[1]}]
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-gray-400 italic">Empty</span>
                )}
              </div>
            </div>

            {/* Final Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Merge Complete!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Merged {currentStepData.intervals.length} intervals into{" "}
                  {currentStepData.result.length}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5 overflow-y-auto max-h-96">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(n log n)
                </h4>
                <p className="text-xs text-gray-600">
                  Dominated by sorting. The merging process is O(n).
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(n)
                </h4>
                <p className="text-xs text-gray-600">
                  Result array to store merged intervals (worst case: no merges)
                </p>
              </div>
            </div>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            <ol className="space-y-1 text-xs text-gray-700">
              <li className="flex">
                <span className="font-medium mr-1">1.</span>
                <span>Sort intervals by their start time</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">2.</span>
                <span>Initialize with the first interval</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">3.</span>
                <span>For each subsequent interval:</span>
              </li>
              <li className="flex ml-4">
                <span className="mr-1">•</span>
                <span>
                  If it overlaps with current (current.end ≥ next.start)
                </span>
              </li>
              <li className="flex ml-4">
                <span className="mr-1">•</span>
                <span>
                  Merge by extending the end to max(current.end, next.end)
                </span>
              </li>
              <li className="flex ml-4">
                <span className="mr-1">•</span>
                <span>Otherwise, add current to result and move to next</span>
              </li>
              <li className="flex">
                <span className="font-medium mr-1">4.</span>
                <span>Add the final interval to result</span>
              </li>
            </ol>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Sorting enables single-pass merging</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Two intervals overlap if: interval1.end ≥ interval2.start
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>When merging, take the maximum of both end points</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Handles all cases: partial overlap, complete containment,
                  adjacent
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The greedy approach (merge when possible) is optimal
                </span>
              </li>
            </ul>
          </div>

          {/* Overlap Cases */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Overlap Cases</h3>
            <div className="space-y-2 text-xs">
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">Case 1: No Overlap</p>
                <p className="font-mono text-gray-600">
                  [1,2] [3,4] → Keep separate
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">Case 2: Partial Overlap</p>
                <p className="font-mono text-gray-600">[1,3] [2,4] → [1,4]</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">Case 3: Complete Containment</p>
                <p className="font-mono text-gray-600">[1,4] [2,3] → [1,4]</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">Case 4: Adjacent (touching)</p>
                <p className="font-mono text-gray-600">[1,2] [2,3] → [1,3]</p>
              </div>
            </div>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-blue-300 border border-blue-400 rounded"></div>
                <span>Input intervals</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-yellow-400 border-2 border-yellow-600 rounded"></div>
                <span>Currently comparing</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-green-400 border-2 border-green-600 rounded"></div>
                <span>Current merged interval</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 bg-green-100 border border-green-300 rounded text-xs">
                  [a,b]
                </div>
                <span>Result array</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeIntervalsVisualizer;
